import React from 'react';
import { Route } from 'react-router-dom';
import {
    apiDocsPlugin,
    ApiExplorerPage
} from '@backstage/plugin-api-docs';
import {
    CatalogEntityPage,
    CatalogIndexPage,
    catalogPlugin,
} from '@backstage/plugin-catalog';
import {
    CatalogImportPage,
    catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import {
    ScaffolderPage,
    scaffolderPlugin
} from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
    TechDocsIndexPage,
    techdocsPlugin,
    TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import {
    githubAuthApiRef,
    gitlabAuthApiRef,
    microsoftAuthApiRef
} from '@backstage/core-plugin-api';
import {
    AlertDisplay,
    OAuthRequestDialog,
    SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import {
    AppRouter,
    FlatRoutes
} from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { HomePage } from './components/home/HomePage';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { irThemeLight } from './theme/irThemeLight';
import { irThemeDark } from './theme/irThemeDark';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { UnifiedThemeProvider } from '@backstage/theme';

const app = createApp({
    apis,
    bindRoutes({bind}) {
        bind(catalogPlugin.externalRoutes, {
            createComponent: scaffolderPlugin.routes.root,
            viewTechDoc: techdocsPlugin.routes.docRoot,
            createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
        });
        bind(apiDocsPlugin.externalRoutes, {
            registerApi: catalogImportPlugin.routes.importPage,
        });
        bind(scaffolderPlugin.externalRoutes, {
            registerComponent: catalogImportPlugin.routes.importPage,
            viewTechDoc: techdocsPlugin.routes.docRoot,
        });
        bind(orgPlugin.externalRoutes, {
            catalogIndex: catalogPlugin.routes.catalogIndex,
        });
    },
    components: {
        SignInPage: props => (
            <SignInPage
                {...props}
                auto
                providers={[
                    {
                        id: 'microsoft-auth-provider',
                        title: 'IR - Connected Platform',
                        message: 'Sign in using SSO',
                        apiRef: microsoftAuthApiRef,
                    },
                    {
                        id: 'gitlab-auth-provider',
                        title: 'GitLab',
                        message: 'Sign in using GitLab',
                        apiRef: gitlabAuthApiRef,
                    },
                    {
                        id: 'github-auth-provider',
                        title: 'GitHub',
                        message: 'Sign in using GitHub',
                        apiRef: githubAuthApiRef,
                    }
                ]}
            />
        ),
    },
    themes: [
        // {
        //   ...themes.light,
        //   id: 'light',
        // },
        // {
        //   ...themes.dark,
        //   id: 'dark',
        // },
        {
            id: 'ir-theme-light',
            title: 'IR Light',
            variant: 'light',
            icon: <LightIcon/>,
            Provider: ({children}) => (
                <UnifiedThemeProvider theme={irThemeLight} children={children}/>
            ),
        }, {
            id: 'ir-theme-dark',
            title: 'IR Dark',
            variant: 'dark',
            icon: <DarkIcon/>,
            Provider: ({children}) => (
                <UnifiedThemeProvider theme={irThemeDark} children={children}/>
            ),
        }]
});

const routes = (
    <FlatRoutes>
        <Route path="/" element={<HomepageCompositionRoot/>}>
            <HomePage/>
        </Route>
        <Route path="/catalog" element={<CatalogIndexPage/>}/>
        <Route
            path="/catalog/:namespace/:kind/:name"
            element={<CatalogEntityPage/>}
        >
            {entityPage}
        </Route>
        <Route path="/docs" element={<TechDocsIndexPage/>}/>
        <Route
            path="/docs/:namespace/:kind/:name/*"
            element={<TechDocsReaderPage/>}
        >
            <TechDocsAddons>
                <ReportIssue/>
                <Mermaid/>
            </TechDocsAddons>
        </Route>
        <Route path="/create" element={<ScaffolderPage/>}/>
        <Route path="/api-docs" element={<ApiExplorerPage/>}/>
        <Route
            path="/catalog-import"
            element={
                <RequirePermission permission={catalogEntityCreatePermission}>
                    <CatalogImportPage/>
                </RequirePermission>
            }
        />
        <Route path="/search" element={<SearchPage/>}>
            {searchPage}
        </Route>
        <Route path="/settings" element={<UserSettingsPage/>}/>
        <Route path="/catalog-graph" element={<CatalogGraphPage/>}/>
    </FlatRoutes>
);

export default app.createRoot(
    <>
        <AlertDisplay/>
        <OAuthRequestDialog/>
        <AppRouter>
            <Root>{routes}</Root>
        </AppRouter>
    </>,
);
